import * as PIXI from 'pixi.js';
import * as particles from "pixi-particles";
import Contract from "slotworks/utils/contract";
import { sequence, parallel } from "slotworks/utils/race-utils";
import Layers from "slotworks/graphics/layers/layers";
import Components from "slotworks/components/components";
import smartButton, { SmartButtonFlag } from "slotworks/smart-button/smart-button";
import gameState from "slotworks/model/game-state";
import RainbowStacksRespinRecord from "integration/revolver/schema/respin-record";
import settingsModel from "slotworks/model/settings/settings-model";
import ButtonElement, { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FreespinRequestPayload from "slotworks/model/gameplay/requests/freespin-request-payload";
import commsManager from "slotworks/server/comms-manager";
import BonusComponent from "./bonus-component";
import * as TweenJS from "slotworks/utils/tween";
import { Sprite } from "pixi.js";
import Timer from "slotworks/utils/timer";
import graphicsManager from "slotworks/graphics/graphics-manager";
import Rectangle from "slotworks/utils/geom/rectangle";
import RainbowStacksSpinRecord from "integration/revolver/schema/spin-record";
import RainbowStacksComponents from "components/components";
import betModel from 'slotworks/model/bets/bet-model';
import { RespinAwardPrompt } from './respin-award-prompt';
import { RespinMeter, PotTrain } from './respin-elements';
import particleManager from 'slotworks/graphics/particles/particle-manager';
import Point from 'slotworks/utils/geom/point';
import { ChancesMeter } from './respin-chances-meter';
import { ShowGameElementsCommand, HideGameElementsCommand } from 'commands/respin-commands';
import deviceInfo from 'slotworks/utils/device-info';
import canvasManager, { Orientation } from 'slotworks/graphics/canvas/canvas-manager';
import getJackpotTypeFromSymbol from 'util/jackpot';
import ExtendedSpine from 'slotworks/graphics/spine/spine';
import { GlowTransitionCommand } from 'commands/glow-transition-command';
import BonusRecord from 'integration/revolver/schema/bonus-record';
import soundManager from 'slotworks/sound/sound-manager';
import LeprechaunComponent from './leprechaun-component';
import AnimatedSprite from 'slotworks/graphics/animation/animated-sprite';
import { Easing } from 'slotworks/utils/tween';

const MAX_RESPINS = 3;

export default class RespinComponent {
    
    private freeRespin: boolean;

    private scene:string;

    private shineRays: Sprite;

    private lastAmount: number = 0;

    private shineRaysEndScale: number = 1;

    private type: string = "";
    private jackpotValue: number = 0;

    private pots: Sprite[] = [];
    private amounts: number[] = [];

    private coins: AnimatedSprite[]= [];
    private coinsScales: number[] = [1, 1, 1];
    private coinGlows: Sprite[] = [];

    private sizeToJackpot: number;

    private door: ExtendedSpine;

    // private glitterEmitter: particles.Emitter;
    private coinEmitter: particles.Emitter;

    private inResolve: (value:void) => void = null;

    private multiplierPrompt: RespinAwardPrompt;
    private respinMeter: RespinMeter;
    private potTrain: PotTrain;

    private chancesMeter: ChancesMeter;
    private leprechaun: LeprechaunComponent;

    private knockArea: PIXI.Container = null;
    private done: boolean = false;

    private rainbowUpEmitter: particles.Emitter;
    private rainbowDownEmitter: particles.Emitter;

    private coinEmitters: particles.Emitter[] = [];
    private coinExplodeEmitters: particles.Emitter[] = [];

    private potContainer: PIXI.Container;

    public start(): Contract<void> {
        this.freeRespin = gameState.getCurrentGame().inFreespinsGame;
        this.scene = this.freeRespin ? "respinfreespin": "respin";
        this.done = false;
        this.skipDone = true;
        const record = gameState.getCurrentGame().getCurrentRecord() as RainbowStacksRespinRecord | RainbowStacksSpinRecord;
        const type = record.jackpotSymbol;

        const recovered = record instanceof RainbowStacksRespinRecord;
       
        switch(type) {
            case "J1": 
                this.type = "grand";
                this.sizeToJackpot = 9;
                break;
            case "J2": 
                this.type = "major";
                this.sizeToJackpot = 8;
                break;
            case "J3": 
                this.type = "maxi";
                this.sizeToJackpot = 7;
                break;
            case "J4": 
                this.type = "mini";
                this.sizeToJackpot = 6;
                break;
        }
        this.jackpotValue = RainbowStacksComponents.jackpotMeters.getJackpotByType(this.type);

        return sequence([
            () => this.displayPrompt(recovered),
            () => Contract.wrap(() => {
                soundManager.customEvent("jpbonus_music");
                this.leprechaun = new LeprechaunComponent();
                deviceInfo.onLayoutChange.add(this.onLayoutChange);
            }),
            () => Contract.getTimeoutContract(500),
            () => this.leprechaun.sayHello(),
            () => parallel([
                () => Contract.wrap(() => { 
                    this.potContainer = new PIXI.Container();
                    Layers.Custom.get("Respin").add(this.potContainer);
                    soundManager.customEvent("jpmeter_rise");
                    this.coinEmitter = this.addParticles(PARTICLE_COIN, Layers.Custom.get("Respin"));
                    this.coinEmitter.emit = false;

                    this.rainbowUpEmitter = this.addParticles(PARTICLE_RAINBOW_UP, Layers.Custom.get("Respin"));
                    this.rainbowUpEmitter.emit = false;

                    this.rainbowDownEmitter = this.addParticles(PARTICLE_RAINBOW_DOWN, Layers.Custom.get("Respin"));
                    this.rainbowDownEmitter.emit = false;
                }),
                () => this.potTrain.moveUp(1, this.coinEmitter),
                () => this.respinMeter.show(),
                () => this.moveCoinFromTrain(0),
                () => this.moveCoinFromTrain(1),
                () => this.moveCoinFromTrain(2),
            ]),
            () => Contract.wrap(() => this.coinEmitter.emit = false),
            () => this.potTrain.moveDown(),
            () => Layers.CelebrationBackground.defaultScene(),
            () => Contract.wrap(() => {
                if(record instanceof RainbowStacksRespinRecord) {
                    this.recoverState();
                    this.displayChances(record.remainingRespins);
                    this.chancesMeter.setValue(record.remainingRespins, true);
                } else {
                    this.lastAmount = 0;
                    this.displayChances(MAX_RESPINS);
                    this.chancesMeter.setValue(MAX_RESPINS, true);
                }
            }),
            () => this.processRespin(),
            () => Contract.wrap(() => {
                const respinRecord = gameState.getCurrentGame().getCurrentRecord() as RainbowStacksRespinRecord;
                if(respinRecord.awardedAmounts.length >= this.sizeToJackpot) {
                    respinRecord.isJackpotWon = true;
                    this.respinMeter.startJackpotCelebration();
                }
            }),
            () => this.displayAward(),
            () => this.finalizeRespin()
        ]);
    }

    private onLayoutChange = () => {
        setTimeout(() => {
            if(canvasManager.orientation == Orientation.PORTRAIT) {
                this.door.state.setAnimation(0, "portrait", false);
            } else {
                this.door.state.setAnimation(0, "landscape", false);
            }

            for(let i = 0; i < this.pots.length; i++) {
                const pot = this.pots[i];
                const target = Layers.Custom.get("Respin").getPosition(`pot_${this.type}_${i + 1}`);
                pot.width = target.width;
                pot.height = target.height;
                pot.position.set(target.x + target.width/2, target.y + target.height/2);
            }

            if(this.knockArea) {
                Layers.Custom.get("RespinTexts").add(this.knockArea);
                const position = Layers.Custom.get("RespinTexts").getPosition("knockArea");
                this.knockArea.hitArea = new PIXI.Rectangle(position.x, position.y, position.width, position.height); 
            }

            if(!this.skipping && !this.skipDone && !this.done) {
                const skipButton = Layers.Custom.get("RespinTexts").getButton("skip");
                if(skipButton) {
                    skipButton.on(ButtonEvent.POINTER_DOWN, this.onSkip);
                }
                if(this.pot) {
                    const position = Layers.Custom.get("Respin").getPosition(this.type + "pot");
                    this.pot.width = position.width;
                    this.pot.height = position.height;
                    this.pot.position.set(position.x + position.width/2, position.y + position.height/2);
                }
            }

            if(canvasManager.orientation == Orientation.LANDSCAPE) {
                const message1 = Layers.Custom.get("RespinBackground").getText("sign_message1");
                const message2 = Layers.Custom.get("RespinBackground").getText("sign_message2");
                const textPosition1 = Layers.Custom.get("RespinBackground").getPosition("text1");
                const textPosition2 = Layers.Custom.get("RespinBackground").getPosition("text2");
                message1.position.set(textPosition1.x, textPosition1.y);
                message1.angle = -8;
                message2.position.set(textPosition2.x, textPosition2.y);
                message2.angle = -8;
            }

            this.updateRainbow();
        }, 10);
    }

    private finalizeRespin() {
        deviceInfo.onLayoutChange.remove(this.onLayoutChange);
        const respinRecord = gameState.getCurrentGame().getCurrentRecord() as RainbowStacksRespinRecord;
        soundManager.customEvent("jpbonus_music_stop");
        if(respinRecord.isJackpotWon) {
            const type = getJackpotTypeFromSymbol(respinRecord);
            return sequence([
                () => this.respinMeter.moveMeterToPlaque(),
                () => Contract.getTimeoutContract(600),
                () => Layers.CelebrationBackground.setScene("jackpot"),
                () => Layers.CelebrationContent.setScene("jackpot_" + type),
                () => Layers.CelebrationForeground.setScene("jackpot"),
                () => parallel([
                    () => this.multiplierPrompt.moveAwardToJackpot(),
                    () => this.respinMeter.moveMeterToJackpot()
                ])
            ]);
        } else {
            return this.celebrateAward();
        }
    }

    private displayPrompt(recovered: boolean) {
        if(recovered) return this.setElements();
        return new Contract(resolve => {
            const scene = gameState.getCurrentGame().inFreespinsGame ? "respinsintro_fs" : "respinsintro";
            const contents = new Map<string, string>();
            Layers.CelebrationBackground.setScene("respins").execute();
            Components.prompt.show([scene], contents).then(() => {
                this.setElements().execute();
                smartButton.setFlag(SmartButtonFlag.PROMPT, false);
                resolve(null);
            });
        });
    }

    public end() {
        const respinRecord = gameState.getCurrentGame().getCurrentRecord() as RainbowStacksRespinRecord;

        const win = respinRecord.totalWon;
        respinRecord.creditsWon = win;
        respinRecord.beenPaid = true;

        const gameplay = gameState.getCurrentGame();
        gameplay.totalCreditsWon += win;
        gameState.updateNetPosition(win);
        if(gameplay.inFreespinsGame) {
            gameplay.totalFreespinsCreditsWon += win;
        } else {
            gameplay.totalBaseCreditsWon += win;
        }
        
        this.destroy();
        smartButton.setFlag(SmartButtonFlag.BONUS, false);
        if(!respinRecord.isJackpotWon) {
            this.hideElements().then(() => {
                if(!deviceInfo.isDesktop) {
                    Layers.Custom.get("Messages")?.defaultScene().execute();
                }
                Components.betBar.setEnabled(true);
                Layers.BetBar.show();
                Layers.SmartButton.show();
                let win;
                if(gameState.getCurrentGame().inFreespinsGame) {
                    soundManager.customEvent("freespins_music");
                    win = gameState.getCurrentGame().getRunningFreespinsWinTotal();
                } else {
                    soundManager.customEvent("base_music");
                    win = gameState.getCurrentGame().totalCreditsWon;
                }
                Components.betBar.winnings.setWinnings(win, win, 750);
            });            
        }
    }

    public destroy() {
        this.respinMeter.destroy();
        this.potTrain.destroy();
        this.chancesMeter.destroy();
    }

    public moveCoinFromTrain(index: number): Contract<void> {
        return new Contract(resolve => {
            const coin = this.coins[index];
            const startPosition =  Layers.Custom.get("Respin").getPosition(`pot_mini_${index + 2}`);
            const endPosition = Layers.Custom.get("Respin").getPosition(`coin${index + 1}`);
            const endScale = this.coinsScales[index];

            coin.scale.set(0.1, 0.1);
            coin.position.set(startPosition.x + startPosition.width/2, startPosition.y);
            const startTime = canvasManager.orientation == Orientation.LANDSCAPE ? 300 : 360;
            
            const emitter = this.getCoinEmitter();
            emitter.emit = false;
            new TweenJS.Tween(coin)
            .to({x: endPosition.x + endPosition.width/2, 
                y: [endPosition.y - 200, endPosition.y + endPosition.height/2],
                rotation: 0}, 350 + index * 20)
            .delay(startTime + index * 100)
            .onStart(() => { 
                coin.visible = true;
                emitter.spawnPos.set(coin.x, coin.y);
                emitter.emit = true;
            })
            .onUpdate(() => {
                emitter.spawnPos.set(coin.x, coin.y);
            })
            .interpolation(TweenJS.Interpolation.Bezier)
            .onComplete(() => {
                this.flashCoinGlow(index);
                const endPosition = Layers.Custom.get("Respin").getPosition(`coin${index + 1}`);
                const endScale = this.coinsScales[index];
                coin.position.set(endPosition.x + endPosition.width/2,  endPosition.y + endPosition.height/2);
                coin.scale.set(endScale, endScale);
                soundManager.customEvent("jp_chances_coin_land");

                emitter.emit = false;
                this.releaseCoinEmitter(emitter);
                
                this.explodeCoinEmitter(coin.x, coin.y);

                resolve(null);
            })
            .start();
            new TweenJS.Tween(coin.scale)
            .to({x: endScale, y: endScale}, 300)
            .start();
        });
    }

    private celebrateAward() {
        return sequence([            
            () => this.multiplierPrompt.celebrate(),
            () => Contract.wrap(() => soundManager.customEvent("jp_standard_result")),
            () => Layers.CelebrationBackground.setScene("respins"),
            () => Layers.CelebrationContent.setScene("respins"),
            () => Contract.getTimeoutContract(2000)
        ]);
    }

    private displayAward() {        
        Layers.Prompts.setScene(this.freeRespin ? "multiplier_fs" : "multiplier").execute();
        this.multiplierPrompt = new RespinAwardPrompt();
        this.multiplierPrompt.init(this.type);
        return sequence([
            () => this.multiplierPrompt.emphasizePots(this.pots),
            () => this.multiplierPrompt.show(),
            () => this.multiplierPrompt.countUpAwards(this.pots, this.amounts),
        ]);
    }

    private recoverState(start: number = 0) {
        const record = gameState.getCurrentGame().latestRecord() as RainbowStacksRespinRecord;
        this.lastAmount = record.awardedAmounts.length;
        const bet = betModel.getTotalBet();
        for(let i = start; i < record.awardedAmounts.length; i++) {
            const multiplierValue = record.awardedAmounts[i];
            const pot = graphicsManager.createSprite(this.type + "pot");
            const target = Layers.Custom.get("Respin").getPosition(`pot_${this.type}_${i + 1}`);    
                
            this.potContainer.addChild(pot);
            pot.anchor.set(0.5, 0.5);            
            pot.width = target.width;
            pot.height = target.height;
            pot.position.set(target.x + target.width/2, target.y + target.height/2);

            const multiplier = graphicsManager.createSprite(multiplierValue + "X");
            multiplier.anchor.set(0.5, 0.5);
            multiplier.y = 50;
            pot.addChild(multiplier);
            
            this.pots.push(pot);
            this.amounts.push(multiplierValue * bet);
        } 
        for(let i = 0; i < start; i++) {
            const pot = this.pots[i];
            const target = Layers.Custom.get("Respin").getPosition(`pot_${this.type}_${i + 1}`);
            TweenJS.removeAll(pot);
            TweenJS.removeAll(pot.scale);
            pot.width = target.width;
            pot.height = target.height;
            pot.position.set(target.x + target.width/2, target.y + target.height/2);
        }
    }

    private processRespin(): Contract<void> {
        return new Contract<void>(resolve => {
            this.inResolve = resolve;

            this.skipping = false;
            this.skipDone = false;

            const skipButton = Layers.Custom.get("RespinTexts").getButton("skip");
            if(skipButton) {
                skipButton.on(ButtonEvent.POINTER_DOWN, this.onSkip);
            }
            
            this.waitForInput();
        })
    }

    private promptUserToKnock() {
        // this.door.visible = true;

        return new Contract(resolve => {
            const elements = [
                Layers.Custom.get("RespinForeground").getSprite("fist_icon"),
                Layers.Custom.get("RespinTexts").getSprite("knock_text"),
                Layers.Custom.get("RespinTexts").getSprite("reveal_text"),
            ];

            for(let el of elements) {
                el.visible = true;
            }

            let tweenObj = { alpha: 0 };
            const update = () => {
                for(let el of elements) {
                    el.alpha = tweenObj.alpha;
                }
            }
            update();
            
            new TweenJS.Tween(tweenObj)
            .to({alpha: 1}, 400)
            .onUpdate(update)
            .onComplete(resolve)
            .start();
        })
    }

    private onDoorClick = () => {
        if(this.done || this.skipDone || this.skipping) return;

        if (this.knockArea) {
            this.knockArea.interactive = false;
            this.knockArea.off(ButtonEvent.POINTER_DOWN.getEventString(), this.onDoorClick);
        }
                    
        parallel([ 
            () => sequence([
                () => this.knock(),
                () => this.hideDoorPrompt()
            ]),
            () => this.sendRespinRequest()
        ]).then(() => {
            this.chancesMeter.decrement();
            this.processRespinResponse();
        });
    };

    private cancelWaitForInput() {
        Timer.clearTimeout(this.knockTimer);
        if (this.knockArea) {
            this.knockArea.interactive = false;
            this.knockArea.off(ButtonEvent.POINTER_DOWN.getEventString(), this.onDoorClick);
        }
    }

    private knockTimer: number;
    private waitForInput() {

        if(this.skipping)
            return;

        this.promptUserToKnock().then(() => {
            this.knockTimer = Timer.setTimeout(() => {

                if(!this.knockArea) {
                    this.knockArea = new PIXI.Container();
                    Layers.Custom.get("RespinTexts").add(this.knockArea);
                }
                const position = Layers.Custom.get("RespinTexts").getPosition("knockArea");
                this.knockArea.hitArea = new PIXI.Rectangle(position.x, position.y, position.width, position.height);
                
                if (this.knockArea) {
                    this.knockArea.interactive = true;
                    this.knockArea.cursor = 'pointer';
                    this.knockArea.on("pointerdown", this.onDoorClick);
                }
            }, 100);
        }); 
    }

    private shineRaysTween: TweenJS.Tween = null;
    private pot: Sprite = null;
    private revealAward(record: RainbowStacksRespinRecord):Contract<void> {
        return new Contract(resolve => {

            this.shineRays.scale.set(0, 0);

            this.doorOpen().then(() => {

                if(record.awardedAmounts.length > this.lastAmount) {
                    soundManager.customEvent("jp_prize_pot_appear");
                    this.shineRays.visible = true;
                    new TweenJS.Tween(this.shineRays.scale)
                    .to({x: this.shineRaysEndScale, y: this.shineRaysEndScale}, 400)
                    .start();
                    if(!this.shineRaysTween) {
                        this.shineRaysTween = new TweenJS.Tween(this.shineRays)
                        .to({rotation: Math.PI * 2}, 10000)
                        .repeat(Infinity);
                    }
                    this.shineRaysTween.start();

                    const type = this.type + "pot";
                    const multiplierValue = record.awardedAmounts[record.awardedAmounts.length - 1];
                    const pot = graphicsManager.createSprite(type);
                    const position = Layers.Custom.get("Respin").getPosition(type);
                    
                    const foregroundPosition = Layers.Custom.get("Respin").localToLocal(Layers.Custom.get("RespinForeground"), position);

                    // this.potContainer.addChild(pot);
                    pot.anchor.set(0.5, 0.5);
                    pot.width = position.width;
                    pot.height = position.height;
                    Layers.Custom.get("RespinForeground").container.addChildAt(pot, 1);
                    pot.position.set(foregroundPosition.x + position.width/2, foregroundPosition.y + position.height/2);

                    const multiplier = graphicsManager.createSprite(multiplierValue + "X");
                    multiplier.anchor.set(0.5, 0.5);
                    multiplier.y = 50;
                    pot.addChild(multiplier);
                    this.pots.push(pot);
                    this.pot = pot;
                    this.amounts.push(multiplierValue * betModel.getTotalBet());

                    this.lastAmount = record.awardedAmounts.length;

                    this.regenerateCoins();

                    Timer.setTimeout(() => {
                        const position = Layers.Custom.get("Respin").getPosition(type);
                        this.potContainer.addChild(pot);
                        pot.position.set(position.x + position.width/2, position.y + position.height/2);

                        new TweenJS.Tween(this.shineRays.scale)
                        .to({x: 0, y: 0}, 200)
                        .onComplete(() => {
                            this.shineRaysTween.stop();
                            this.shineRays.visible = false;
                        }).start();
                        sequence([
                            () => this.movePot(pot, this.type, this.lastAmount),
                            () => Contract.getTimeoutContract(100),
                            () => this.potTrain.moveUp(0.5, null, this.lastAmount - 1),
                            () => Contract.getTimeoutContract(100),
                            () => this.potTrain.moveDown()
                        ]).then(resolve);
                    }, 1000);

                    this.chancesMeter.maxChances();

                    this.leprechaun.win().execute();
                } else {
                    Timer.setTimeout(() => {
                        this.leprechaun.noWin().execute();
                        this.flipCoinOnEmpty().execute();

                        this.shineRays.visible = false;
                        Timer.setTimeout(resolve, 2000);
                    }, 1000);
                }    
            });        
        })
    }

    private regenerateCoins() {
        for(let i = 0; i < this.coins.length; i++) {
            if(!this.coins[i].visible) {
                this.moveCoinOnPosition(i).execute();
            }
        }
    }

    private moveCoinOnPosition(index: number): Contract<void> {
        return new Contract(resolve => {
            const coin = this.coins[index];
            const startPosition = Layers.Custom.get("Respin").getPosition("pot");
            const endPosition = Layers.Custom.get("Respin").getPosition(`coin${index + 1}`);

            coin.scale.set(0.1, 0.1);
            coin.position.set(startPosition.x, startPosition.y);
            const endScale = this.coinsScales[index];
            coin.visible = true;

            const emitter = this.getCoinEmitter();
            emitter.emit = true;
            emitter.spawnPos.set(coin.x, coin.y);

            new TweenJS.Tween(coin)
            .to({x: endPosition.x + endPosition.width/2, 
                y: [endPosition.y - 200, endPosition.y + endPosition.height/2],
                rotation: 0}, 350 + index * 20)
            .interpolation(TweenJS.Interpolation.Bezier)
            .onUpdate(() => emitter.spawnPos.set(coin.x, coin.y))
            .onComplete(() => {
                this.flashCoinGlow(index);
                const endPosition = Layers.Custom.get("Respin").getPosition(`coin${index + 1}`);
                const endScale = this.coinsScales[index];
                coin.position.set(endPosition.x + endPosition.width/2, endPosition.y + endPosition.height/2);
                coin.scale.set(endScale, endScale);
                soundManager.customEvent("jp_chances_coin_land");
                this.releaseCoinEmitter(emitter);
                this.explodeCoinEmitter(coin.x, coin.y);
                resolve(null);
            })
            .start();
            new TweenJS.Tween(coin.scale)
            .to({x: endScale, y: endScale}, 300)
            .start();
        })
    }

    private moveToTop(sprite: Sprite) {
        const parent = sprite.parent;
        parent.removeChild(sprite);
        parent.addChild(sprite);
    }

    private flipCoinOnEmpty():Contract<void> {
        return new Contract(resolve => {
            let coin: AnimatedSprite;
            let i;
            for(i = this.coins.length - 1; i >= 0; i--) {
                if(i == 0 || this.coins[i].visible) {
                    coin = this.coins[i];
                    break;
                }
            }
            coin.visible = true;

            const middleX = coin.x - 200;
            const middleY = coin.y - 400;
            const bottomX = coin.x - 400;
            const bottomY = canvasManager.stageHeight + 50;

            const time = canvasManager.orientation == Orientation.PORTRAIT ? 1000 : 800;

            const emitter = this.getCoinEmitter();
            emitter.emit = true;
            emitter.spawnPos.set(coin.x, coin.y);
            this.moveToTop(coin);
            soundManager.customEvent("jp_change_coin_lose");
            new TweenJS.Tween(coin)
            .to({x: [middleX, bottomX], y: [middleY, bottomY], rotation: -2 * Math.PI}, time)
            .interpolation(TweenJS.Interpolation.Bezier)
            .onUpdate(() => emitter.spawnPos.set(coin.x, coin.y))
            .onComplete(() => {
                coin.visible = false;
                this.releaseCoinEmitter(emitter);
                resolve(null);
            })
            .start();
        });
    }

    private flashCoinGlow(index: number) {
        // coinGlows
        const glow = this.coinGlows[index];
        glow.visible = true;
        glow.scale.set(0.9, 0.9);
        new TweenJS.Tween(glow.scale)
        .to({x: 1.1, y: 1.1}, 150)
        .easing(Easing.Back.Out)
        .onComplete(() => {
            new TweenJS.Tween(glow.scale)
            .to({x: 0, y: 0}, 150)
            .easing(Easing.Back.In)
            .onComplete(() => {
                glow.visible = false;
            })
            .start();
        })
        .start();
    }

    private moveRainbowEmitter() {
        const start = Layers.Custom.get("Respin").getPosition("minipot");
        const end = Layers.Custom.get("Respin").getPosition("pot_mini_1");
        const topY = canvasManager.orientation == Orientation.PORTRAIT ? -start.y - 3000 : -start.y - 2000;
        const obj = {x: start.x, y: start.y};
        this.rainbowUpEmitter.emit = true;
        this.rainbowUpEmitter.spawnPos.set(obj.x, obj.y);
        new TweenJS.Tween(obj)
        .to({y: [topY]}, 500)  
        .onUpdate(() => {
            this.rainbowUpEmitter.spawnPos.set(obj.x, obj.y);
        })
        .onComplete(() => {
            this.rainbowUpEmitter.emit = false;

            obj.x = end.x;
            obj.y = topY;
            this.rainbowDownEmitter.emit = true;
            this.rainbowDownEmitter.spawnPos.set(obj.x, obj.y);
            new TweenJS.Tween(obj)
            .to({y: end.y + end.height}, 500)   
            .onUpdate(() => {
                this.rainbowDownEmitter.spawnPos.set(obj.x, obj.y);
            })
            .onComplete(() => {
                this.rainbowDownEmitter.emit = false;
            })
            .start();

        })
        .start();
    }

    private movePot(pot: Sprite, type: string, lastAmount: number) {
        return new Contract(resolve => {
            this.moveRainbowEmitter();
            Timer.setTimeout(() => this.moveRainbowEmitter(), 600);
            
            this.showRainbow();

            Timer.setTimeout(() => {                
                soundManager.customEvent("jp_pot_fly");
                sequence([
                    () => this.movePotUp(pot),
                    () => Contract.getTimeoutContract(60),
                    () => this.movePotDown(pot, type, lastAmount),
                    () => this.movePotBounce(pot, type, lastAmount),
                ]).then(resolve);
            }, 800);
        })
    }

    private movePotUp(pot: Sprite) {
        return new Contract(resolve => {
            const topY = canvasManager.orientation == Orientation.PORTRAIT ? pot.y - 1800 : pot.y - 1000;
            const position = Layers.Custom.get("Respin").getPosition(this.type + "pot");
            this.pot = null;
            pot.width = position.width;
            pot.height = position.height;
            pot.position.set(position.x + position.width/2, position.y + position.height/2);
            const scale = pot.scale.x * 0.7;
            new TweenJS.Tween(pot.scale)
            .to({x: scale, y: scale}, 300)
            .start();
            new TweenJS.Tween(pot)
            .to({x: [pot.x, pot.x - 360], y: [topY, topY]}, 400)
            .easing(TweenJS.Easing.Cubic.In)
            .interpolation(TweenJS.Interpolation.Bezier)
            .onComplete(resolve)
            .start();
        })
    }

    private movePotDown(pot: Sprite, type: string, lastAmount: number) {
        return new Contract(resolve => {
            const position = Layers.Custom.get("Respin").getPosition(`pot_${type}_${lastAmount}`);
            const topY = canvasManager.orientation == Orientation.PORTRAIT ? position.y - 1800 : position.y - 1000;
            pot.position.set(position.x + position.width/2, topY);
            pot.width = position.width;
            pot.height = position.height;
            const time = (position.y - topY) * 0.5;
            new TweenJS.Tween(pot)
            .to({x: position.x + position.width/2, y: position.y + position.height/2}, time)
            .onComplete(resolve)
            .start();
        })
    }

    private movePotBounce(pot: Sprite, type: string, lastAmount: number) {
        return new Contract(resolve => {
            const position = Layers.Custom.get("Respin").getPosition(`pot_${type}_${lastAmount}`);
            pot.position.set(position.x + position.width/2, position.y + position.height/2);
            pot.width = position.width;
            pot.height = position.height;
            const bounce = canvasManager.orientation == Orientation.PORTRAIT ? 40 : 20;
            soundManager.customEvent("jp_pot_land");
            this.coinEmitter.spawnPos.set(pot.x, pot.y - 30);
            this.coinEmitter.emit = true;
            new TweenJS.Tween(pot)
            .to({y: position.y + position.height/2 - bounce}, 80)
            .onUpdate(() => {
                this.coinEmitter.spawnPos.set(pot.x, pot.y - 80);
            })
            .repeat(1)
            .yoyo(true)
            .onComplete(() => {
                const position = Layers.Custom.get("Respin").getPosition(`pot_${type}_${lastAmount}`);
                pot.position.set(position.x + position.width/2, position.y + position.height/2);
                pot.width = position.width;
                pot.height = position.height;
                Timer.setTimeout(() => this.coinEmitter.emit = false, 100);
                resolve(null);
            })
            .start();

            
    
        })
    }

    private updateRainbow() {
        const rainbow = Layers.Custom.get("Respin").getSpine("rainbow");
        if(rainbow) {
            if(canvasManager.orientation == Orientation.LANDSCAPE) {
                rainbow.setSkin("landscape");
                rainbow.scale.x = 1;
                rainbow.scale.y = 1;
            } else {
                rainbow.setSkin("portrait");
                if(deviceInfo.widePortrait) {
                    rainbow.scale.x = 0.86;
                    rainbow.scale.y = 0.86;
                }
            }
        }
    }

    private showRainbow() {
        soundManager.customEvent("jp_rainbow_appear");
        this.updateRainbow();
        const rainbow = Layers.Custom.get("Respin").getSpine("rainbow");
        rainbow.play(1, "rainbow_in_out");
    }

    private sendRespinRequest() {
        const request = new FreespinRequestPayload();
        return commsManager.request(request);
    }

    private cancelSkip() {
        const skipButton = Layers.Custom.get("RespinTexts").getButton("skip");
        if(skipButton) {
            skipButton.off(ButtonEvent.POINTER_DOWN, this.onSkip);
            skipButton.setEnabled(false);
        }
    }

    private processRespinResponse() {
        const record = gameState.getCurrentGame().latestRecord() as RainbowStacksRespinRecord;        
        // this.displayChances(record.remainingRespins);

        this.revealAward(record).then(() => {
            if(record.isComplete) {
                this.done = true;
                this.cancelSkip();
                this.inResolve(null);
            } else if(record.triggersBonus) {
                this.doorClose().then(() => this.startBonus());    
            } else {
                this.doorClose().then(() => this.waitForInput());
            }
        });
    }

    private showBonusCoins() {
        return new Contract(resolve => { 
            const bonusRecord = gameState.getCurrentGame().latestRecord() as BonusRecord;
            const chances = bonusRecord.retriggers;

            this.leprechaun.extraChance().execute();
            const popfuncs: (() => Contract<void>)[] = [];
            const funcs: (() => Contract<void>)[] = [];
            for(let i = 1; i <= chances; i++) {
                const coin = this.coins[i - 1];
                const shine = Layers.Custom.get("Respin").getSprite(`shine${i}`);
                const position = Layers.Custom.get("Respin").getPosition(`award_coin${i}`);
                const shineScale = shine.scale.x;

                coin.position.set(position.x + position.width/2, position.y + position.height/2);
                coin.scale.set(0, 0);
                coin.visible = true;
                shine.anchor.set(0.5, 0.5);
                shine.x += shine.width/2;
                shine.y += shine.height/2;
                shine.scale.set(0, 0);

                popfuncs.push(() => new Contract(resolve => {
                    new TweenJS.Tween(coin)
                    .to({y: position.y - 150}, 200)
                    .repeat(1)
                    .yoyo(true)
                    .onComplete(resolve)
                    .start();
                    new TweenJS.Tween(coin.scale)
                    .to({x: this.coinsScales[i - 1], y: this.coinsScales[i - 1]}, 100)
                    .easing(Easing.Back.Out)
                    .start();
                    shine.visible = true;
                    new TweenJS.Tween(shine.scale)
                    .to({x: shineScale, y: shineScale}, 200)
                    .easing(Easing.Back.Out)
                    .onComplete(() => {
                        new TweenJS.Tween(shine.scale)
                        .to({x: 0, y: 0}, 100)
                        .delay(100)
                        .onComplete(() => shine.visible = false)
                        .start();
                    })
                    .start();
                    this.explodeCoinEmitter(position.x + position.width/2, position.y + position.height/2);
                }));

                const pos = i - 1;
                funcs.push(() => this.moveBonusCoinsToPositions(pos));
            }
            sequence([
                () => Contract.getTimeoutContract(500),
                () => parallel(popfuncs),
                () => Contract.getTimeoutContract(200),
                () => parallel(funcs)
            ]).then(resolve);
        });
    }

    private moveBonusCoinsToPositions(index: number): Contract<void> {
        return new Contract(resolve => {
            const coin = this.coins[index];
            const endPosition = Layers.Custom.get("Respin").getPosition(`coin${index + 1}`);
            coin.visible = true;
            new TweenJS.Tween(coin)
            .to({x: endPosition.x + endPosition.width/2, 
                y: [endPosition.y - 200, endPosition.y + endPosition.height/2],
                rotation: 0}, 350 + index * 20)
            .interpolation(TweenJS.Interpolation.Bezier)
            .onComplete(() => {
                this.flashCoinGlow(index);
                const endPosition = Layers.Custom.get("Respin").getPosition(`coin${index + 1}`);
                const endScale = this.coinsScales[index];
                coin.position.set(endPosition.x + endPosition.width/2,  endPosition.y + endPosition.height/2);
                coin.scale.set(endScale, endScale);
                soundManager.customEvent("jp_chances_coin_land");
                resolve(null);
            })
            .start();
        });
    }

    private startBonus() {
        const bonus = new BonusComponent();
        sequence([
            () => bonus.sendBonusRequest(),
            () => parallel([
                () => bonus.displayAward(),
                () => Contract.getDelayedContract(1800, () => this.showBonusCoins())
            ])
        ]).then(() => {
            const bonusRecord = gameState.getCurrentGame().latestRecord() as BonusRecord;
            // show values
            this.displayChances(bonusRecord.retriggers);
            this.chancesMeter.setValue(bonusRecord.retriggers, true);
            this.waitForInput();
        })
    }

    private setElements(): Contract<void> {
        Components.betBar.setEnabled(false);
        smartButton.setFlag(SmartButtonFlag.BONUS, true);
        Layers.BetBar.hide();
        Layers.SmartButton.hide();
        const metersScene = `respin_${this.type}`; 
        this.respinMeter = new RespinMeter();
        this.potTrain = new PotTrain(this.type, this.sizeToJackpot);
        Layers.Background.setScene(this.scene).execute();
        HideGameElementsCommand();
        if(!deviceInfo.isDesktop) {
            Layers.Custom.get("Messages")?.setScene(this.freeRespin ? "respinfreespin" : "respin").execute();
        }
        return sequence([
            () => Layers.Custom.get("River").setScene(this.scene),
            () => Layers.Custom.get("RespinBackground").setScene(this.scene),
            () => Layers.Custom.get("RespinForeground").setScene(this.scene),
            () => Layers.Custom.get("RespinTexts").setScene(this.scene),
            () => this.respinMeter.init(metersScene),
            () => Layers.Custom.get("Respin").setScene("respin"),
            () => Layers.Custom.get("Leprechaun").setScene(this.freeRespin ? "freespins" : "base"),
            () => Contract.wrap(() => {
                Layers.Custom.get("RespinForeground").getSprite("fist_icon").visible = false;
                Layers.Custom.get("RespinTexts").getSprite("knock_text").visible = false;
                Layers.Custom.get("RespinTexts").getSprite("reveal_text").visible = false;

                this.respinMeter.setJackpotValue(this.jackpotValue);
                this.potTrain.init();
                this.chancesMeter = new ChancesMeter();
                this.shineRays = Layers.Custom.get("RespinForeground").getSprite("shinerays");
                this.shineRaysEndScale = this.shineRays.scale.x;
                this.shineRays.anchor.set(0.5, 0.5);
                this.shineRays.x += this.shineRays.width * 0.5;
                this.shineRays.y += this.shineRays.height * 0.5;
                this.shineRays.scale.set(0, 0);
                this.shineRays.visible = false;

                const bottomShine = Layers.Custom.get("Respin").getSprite("pot_shine_trail");
                if(bottomShine) bottomShine.visible = false;

                const mask = Layers.Custom.get("RespinForeground").getSprite("mask");
                mask.visible = false;
                this.door = Layers.Custom.get("RespinForeground").getSpine("door");
                if(gameState.getCurrentGame().inFreespinsGame)
                    this.door.skeleton.setSkinByName("freespins");
                else
                    this.door.skeleton.setSkinByName("base");
                if(canvasManager.orientation == Orientation.PORTRAIT) {
                    this.door.state.setAnimation(0, "portrait", false);
                }
                this.door.state.setAnimation(1, "door_close_still", false);

                for(let i = 1; i <= 3; i++) {
                    this.coins.push(Layers.Custom.get("Respin").getAnimatedSprite(`gold_coin${i}`));
                    this.coinsScales[i - 1] = this.coins[ i - 1].scale.x;
                    this.coins[ i - 1].anchor.set(0.5, 0.5);
                    this.coins[ i - 1].x += this.coins[ i - 1].width/2;
                    this.coins[ i - 1].y += this.coins[ i - 1].height/2;
                    this.coins[ i - 1].gotoAndPlay(this.coins[ i - 1].totalFrames * Math.random());
                    this.coins[ i - 1].visible = false;

                    this.coinGlows.push(Layers.Custom.get("Respin").getAnimatedSprite(`revealed_pot_shine${i}`));
                    this.coinGlows[i - 1].anchor.set(0.5, 0.5);
                    this.coinGlows[i - 1].x += this.coinGlows[i - 1].width/2;
                    this.coinGlows[i - 1].y += this.coinGlows[i - 1].height/2;
                    this.coinGlows[i - 1].visible = false;

                    Layers.Custom.get("Respin").getSprite(`shine${i}`).visible = false;
                }

                if(canvasManager.orientation == Orientation.LANDSCAPE) {
                    const message1 = Layers.Custom.get("RespinBackground").getText("sign_message1");
                    const message2 = Layers.Custom.get("RespinBackground").getText("sign_message2");
                    const textPosition1 = Layers.Custom.get("RespinBackground").getPosition("text1");
                    const textPosition2 = Layers.Custom.get("RespinBackground").getPosition("text2");
                    message1.position.set(textPosition1.x, textPosition1.y);
                    message1.angle = -8;
                    message2.position.set(textPosition2.x, textPosition2.y);
                    message2.angle = -8;
                }
            }),
        ]);
    }

    private explodeCoinEmitter(x: number, y: number) {
        let emitter = this.coinExplodeEmitters.pop();

        if(!emitter) {
            emitter = this.addParticles(PARTICLE_GLITTER_EXPLOSION, Layers.Custom.get("Respin"));
        }

        emitter.spawnPos.set(x, y);
        emitter.emit = true;

        setTimeout(() => {
            emitter.emit = false;
            this.coinExplodeEmitters.push(emitter);
        },400)
    }

    private getCoinEmitter(): particles.Emitter {
        let emitter = this.coinEmitters.pop();

        if(!emitter) {
            emitter = this.addParticles(PARTICLE_COIN_TRAIL, Layers.Custom.get("Respin"));
        }

        return emitter;
    }

    private releaseCoinEmitter(emitter: particles.Emitter) {
        emitter.emit = false;
        this.coinEmitters.push(emitter);
    }

    private hideElements(): Contract<void> {
        GlowTransitionCommand().execute();
        return parallel([
            () => sequence([
                () => Contract.getTimeoutContract(100),
                () => Contract.wrap(() => {                    
                    ShowGameElementsCommand();
                    this.multiplierPrompt.destroy();
                    this.multiplierPrompt = null;
                }),
                () => Layers.CelebrationContent.defaultScene(),
                () => Layers.CelebrationBackground.defaultScene(),
                () => Layers.Prompts.defaultScene()
            ])
        ]);
        
    }

    private displayChances(remain: number) {
        for(let i = 0; i < this.coins.length; i++) {
            this.coins[i].visible = i < remain;
        }
    }

    private doorOpen():Contract<void> {
        return new Contract(resolve => {
            soundManager.customEvent("jpdoor_open");
            this.door.state.setAnimation(1, "door_open", false);
            resolve(null);
        });
    }
    
    private doorClose(): Contract<void> {
        return new Contract(resolve => {
            soundManager.customEvent("jpdoor_close");
            this.door.state.setAnimation(1, "door_close", false);
            this.door.state.tracks[1].listener = {
                complete: () => {
                    this.door.state.setAnimation(1, "door_close_still", false);
                    resolve(null);
                }
            }            
        });
    }

    private addParticles(id: string, layer: Layers) {
        const container = new PIXI.Container();
        const particle = particleManager.add(id, new Point(0, 0), container);

        layer.addParticle(id, particle);
        layer.container.addChild(container);

        return layer.getParticle(id);
    }

    private knock() {
        return new Contract(resolve => {
            const hand = Layers.Custom.get("RespinForeground").getSprite("fist_icon");
            const currentScale = hand.scale.x;
            const scale = currentScale * 0.8;
            new TweenJS.Tween(hand.scale)
            .to({x: scale, y: scale}, 150)
            .yoyo(true)
            .repeat(3)
            .onComplete(resolve)
            .start();
            soundManager.customEvent("jpdoor_knock");
            Timer.setTimeout(() => soundManager.customEvent("jpdoor_knock"), 300);
        });
    }

    private hideDoorPrompt() {
        return new Contract(resolve => {
            const elements = [
                Layers.Custom.get("RespinForeground").getSprite("fist_icon"),
                Layers.Custom.get("RespinTexts").getSprite("knock_text"),
                Layers.Custom.get("RespinTexts").getSprite("reveal_text")
            ];

            let tweenObj = { alpha: 1 };
            const update = () => {
                for(let el of elements) {
                    el.alpha = tweenObj.alpha;
                }
            }
            
            new TweenJS.Tween(tweenObj)
            .to({alpha: 0}, 400)
            .onUpdate(update)
            .onComplete(() => {
                for(let el of elements) {
                    el.visible = false;
                }
                resolve(null);
            })
            .start();
        })
    }

    private skipDone: boolean = false;
    private skipping: boolean = false;
    private skipResolve: (value: void) => void = null;

    private onSkip = () => {
        if(!this.skipping) {
            this.skipping = true;

            const skipButton = Layers.Custom.get("RespinTexts").getButton("skip");
            if(skipButton) {
                skipButton.off(ButtonEvent.POINTER_DOWN, this.onSkip);
                skipButton.setEnabled(false);
            }

            this.cancelAllActions();

            if(this.done) {
                if(this.inResolve) {
                    this.inResolve();
                    this.inResolve = null;
                }
            } else {
                this.makeRequestsAndProcessResponse().then(() => {
                    this.fillInPots();
                    this.positionCoinsOnSkip();

                    this.skipDone = true;

                    if(this.inResolve) {
                        this.inResolve();
                        this.inResolve = null;
                    }
                });
            }
        }
    }

    private positionCoinsOnSkip() {
        const record = gameState.getCurrentGame().getCurrentRecord() as RainbowStacksRespinRecord;
        const isJackpot = record && record.awardedAmounts.length >= this.sizeToJackpot;
        for(let index = 0; index < this.coins.length; index++) {
            const coin = this.coins[index];
            TweenJS.removeAll(coin);
            TweenJS.removeAll(coin.scale);
            coin.visible = isJackpot;
            const endPosition = Layers.Custom.get("Respin").getPosition(`coin${index + 1}`);
            const endScale = this.coinsScales[index];
            coin.position.set(endPosition.x + endPosition.width/2,  endPosition.y + endPosition.height/2);
            coin.scale.set(endScale, endScale);
        }
    }

    private cancelAllActions() {
        this.cancelWaitForInput();
    }

    private makeRequestsAndProcessResponse(): Contract<void> {
        return new Contract(resolve => {
            this.skipResolve = resolve;
            this.makeSkipRequest();
        });
    }

    private makeSkipRequest() {
        this.sendRespinRequest().then(this.processSkipResponse);
    }

    private fillInPots() {
        this.recoverState(this.pots.length);
    }

    private processSkipResponse = () => {
        const record = gameState.getCurrentGame().latestRecord() as RainbowStacksRespinRecord;

        if(record.isComplete) {
            if(this.skipResolve) {
                this.skipResolve();
                this.skipResolve = null;
            }
        } else {
            this.makeSkipRequest();
        }
    }
}

const PARTICLE_GLITTER = "respinGlitter";
const PARTICLE_COIN = "respinCoin";
const PARTICLE_COIN_TRAIL = "coin_trail";
const PARTICLE_GLITTER_EXPLOSION = "glitter_explosion";
const PARTICLE_RAINBOW_UP = "glitter_rainbow_up";
const PARTICLE_RAINBOW_DOWN = "glitter_rainbow_down";


// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/components/respin/respin-component.ts